/* eslint-disable import/max-dependencies */
import { useMutation, useQuery } from '@apollo/client'
import { BodyText, Button, Col, HeadingText, Row } from '@paypalcorp/pp-react'
import { navigate } from '@reach/router'
import { merge } from 'lodash-es'
import AddressCard from 'ppbl/components/AddressCard'
import SpinnerAdapter from 'ppbl/components/SpinnerAdapter'
import InfoText from 'ppbl/components/InfoText'
import { updateApplicationCachedData } from 'ppbl/graphql/__utils__'
import {
  updateApplicationMutation,
  normalizeAddressMutation,
} from 'ppbl/graphql/mutations'
import { getCreditApplicationDataQuery } from 'ppbl/graphql/queries'
import { useApplicationContext, useExperiments } from 'ppbl/hooks'
import clientLogger from 'ppbl/utils/client-logger'
import serverData from 'ppbl/utils/server-data'
import { trackClick, trackPage } from 'ppbl/utils/fpti-helper'
import { FPTI_EVENTS } from 'ppbl/utils/fptiEventNames'
import { getLocalizedText } from 'ppbl/utils/locale-helper'
import { santizeInputFields } from 'ppbl/utils/string-helper'
import { scrollOnErrors } from 'ppbl/utils/validation-helper'
import React, { useEffect, useRef, useState } from 'react'
import { Field, Form } from 'react-final-form'
import {
  scrollToTopOnContinue,
  getApplicationFieldsData,
  updateApplicationPayloadGeneratorPage3,
  handleGraphqlError,
} from 'ppbl/screens/__utils__'
import {
  applicationPage3Fields,
  applicationPage3FieldsApp,
  getApplicationFields,
} from '../applicationFieldMapper'
import './styles.css'
import { cloneAndMask } from 'ppbl/utils/data-masker'
import createDecorator from 'ppbl/utils/form/decorator'
import AddressConfirmationDialog from 'ppbl/components/AddressConfimationDialog'
import { AddressType } from '../../../types'
import { addressDialogConstants } from 'ppbl/utils/constants'
import {
  WorldReady,
  AddressFormat,
  PhoneNumberFormat,
} from '@paypalcorp/worldready'
import { businessWebAddress } from '../applicationFields'
import PreApprovalBanner from 'ppbl/components/PreApprovalBanner'

type Props = {
  applicationType: string
  creditApplicationLoading: boolean
}

const fptiData = {
  page: 'applicationPage3',
  event_name: FPTI_EVENTS.PAGE.PAGE3,
}

const ApplicationPage3: React.FC<Props> = ({
  applicationType,
  creditApplicationLoading,
}) => {
  const { data, updateApplicationContext } = useApplicationContext()

  const experiments = useExperiments()
  const { ppbl_application_fields_experiment = 'control' } = experiments

  const [isAddressNormalizationDialog, setIsAddressNormalizationDialog] =
    useState<boolean>(false)
  const [suggestedAddress, setSuggestedAddress] = useState()
  const [userAddress, setUserAddress] = useState<AddressType>()
  const [applicationFormData, setApplicationFormData] = useState()

  useEffect(() => {
    trackPage(
      merge(fptiData, {
        applicationId: data?.applicationMetaData?.applicationId,
      }),
    )
    clientLogger.info(
      'Application Page 3 is loaded - Application ID:',
      data?.applicationMetaData?.applicationId,
    )
  }, [])

  const { newApplicationPage3Fields } = getApplicationFields(applicationType, experiments)

  const isPaypalApplication = applicationType === 'paypal'

  const renewalToken = serverData.startParams?.renewalToken

  const isAppApplication = applicationType === 'app'

  const { data: cachedApplicationData } = useQuery(
    getCreditApplicationDataQuery,
  )

  const [changeApplicationFields, setChangeApplicationFields] = useState(
    cachedApplicationData.businessAddress1 === null ||
      cachedApplicationData.businessCity === null ||
      cachedApplicationData.businessState === null ||
      cachedApplicationData.businessZip === null,
  )

  const getApplication3Fields = () => {
    const fields = isAppApplication ? applicationPage3FieldsApp : applicationPage3Fields;

    const hideBusinessWebAddress = ppbl_application_fields_experiment === 'treatment_1'

    if (hideBusinessWebAddress) {
      return fields.filter((field) => field !== businessWebAddress)
    }

    return fields
  }

  const [applicationPageFields, setApplicationPageFields] = useState(
    changeApplicationFields
      ? getApplication3Fields()
      : newApplicationPage3Fields,
  )

  const [updateApplication, { loading }] = useMutation(
    updateApplicationMutation,
    {
      onCompleted(response) {
        clientLogger.info(
          'Response received after updating business location in application page 3',
          cloneAndMask(response),
        )
        sessionStorage.setItem('mostRecentCompletedPage', 'applicationPage3')
        updateApplicationContext({
          applicationMetaData: {
            ...data.applicationMetaData,
            mostRecentCompletedPage: 'applicationPage3',
          },
        })
        navigate(
          `/merchant-lending/loanbuilder/application/us/${applicationType}/applicationPage4`,
        )
      },
      onError(updateMutatioError) {
        handleGraphqlError(
          updateMutatioError,
          'Error encountered while updating business location in application page 3',
          'ApplicationPage 3 - updateApplicationMutation',
          data?.applicationMetaData?.applicationId,
          renewalToken,
          data?.applicationMetaData?.legalBusinessName,
          applicationType,
        )
      },
    },
  )

  const [generateNormalizedAddress, { loading: addressNormalizationLoading }] =
    useMutation(normalizeAddressMutation, {
      onCompleted(response) {
        clientLogger.info(
          'Response received for normalized address in application page 3',
          cloneAndMask(response),
        )
        if (
          response?.credit?.businessCredit?.normalizeAddress?.status ===
          'VERIFIED'
        ) {
          updateApplicationCachedData(
            applicationFormData,
            cachedApplicationData,
          )
          updateApplication({
            variables: {
              updateInput: updateApplicationPayloadGeneratorPage3(
                applicationFormData,
                cachedApplicationData.legalBusinessName,
                data.applicationMetaData.applicationId,
              ),
            },
          })
        } else {
          setIsAddressNormalizationDialog(true)
          if (response?.credit?.businessCredit?.normalizeAddress?.address) {
            setSuggestedAddress(
              response?.credit?.businessCredit?.normalizeAddress,
            )
          }
        }
      },
      onError: (error) => {
        handleGraphqlError(
          error,
          'Error encountered while calling address normalization in application 3',
          'ApplicationPage 3 - normalizeAddressMutation',
          data?.applicationMetaData?.applicationId,
          renewalToken,
          data?.applicationMetaData?.legalBusinessName,
          applicationType,
        )
      },
    })

  const handleAddressOnChange = () => {
    trackClick(
      merge(fptiData, {
        component: 'applicationPage3_change_button',
        link: 'applicationPage3_change_button',
        applicationId: data?.applicationMetaData?.applicationId,
        event_name: FPTI_EVENTS.CLICK.NAVIGATION.PAGE_CHANGE,
      }),
    )
    setChangeApplicationFields(true)
    setApplicationPageFields(getApplication3Fields())
  }

  const handleAddressNormalization = (address: any) => {
    clientLogger.info(
      'application page 3 address normalization - Application ID:',
      data?.applicationMetaData?.applicationId,
    )
    generateNormalizedAddress({
      variables: {
        addressInput: {
          address: {
            addressLine1: address?.addressLine1,
            addressLine2: address?.addressLine2,
            adminArea2: address?.adminArea2,
            adminArea1: address?.adminArea1,
            postalCode: address?.postalCode,
            countryCode: address?.countryCode || 'US',
          },
        },
      },
    })
  }

  const handleApplicationSubmit = async (values: any) => {
    trackClick(
      merge(fptiData, {
        component: 'applicationPage3_continue_button',
        link: 'applicationPage3_continue_button',
        applicationId: data?.applicationMetaData?.applicationId,
        event_name: FPTI_EVENTS.CLICK.PAGE_CONTINUE.PAGE3,
      }),
    )

    scrollToTopOnContinue()

    clientLogger.info(
      'application page 3 submitted - Application ID:',
      data?.applicationMetaData?.applicationId,
    )
    clientLogger.info(
      'Legal bussiness name for the application',
      data?.applicationMetaData?.legalBusinessName,
    )
    if (isPaypalApplication) {
      clientLogger.info(
        'paypal users encrypted account number',
        serverData?.user?.encryptedAccountNumber,
      )
    } else if (renewalToken) {
      clientLogger.info('renewal user renewal token', renewalToken)
    }

    const applicationValues = getApplicationFieldsData(
      applicationPage3Fields,
      cachedApplicationData,
      values,
    )

    //sanitize the input values to prevent XSS
    santizeInputFields(applicationValues)

    const address = {
      addressLine1: applicationValues?.businessAddress1,
      addressLine2: applicationValues?.businessAddress2,
      adminArea1: applicationValues?.businessState,
      adminArea2: applicationValues?.businessCity,
      postalCode: applicationValues?.businessZip,
      countryCode: 'US',
    }
    handleAddressNormalization(address)
    setUserAddress(address)
    setApplicationFormData(applicationValues)
  }

  useEffect(() => {
    const worldReadyLocale = serverData.locality.locale
    const worldReady = new WorldReady(worldReadyLocale)
    const fieldsToUpdate: Record<string, string> = {}
    if (applicationType !== 'paypal' && applicationType !== 'app') {
      const phoneNumberFormatter = new PhoneNumberFormat({
        phoneRegion: 'US',
      })
      const isPhoneValid = phoneNumberFormatter.parse(
        cachedApplicationData?.businessPhoneNumber || '',
      )
      const addressFormatter = new AddressFormat(worldReady, {
        addressRegion: 'US',
      })
      const addressKeyMap: Record<string, string> = {
        addressLine1: 'businessAddress1',
        addressLine2: 'businessAddress2',
        adminArea1: 'businessState',
        adminArea2: 'businessState',
        postalCode: 'businessZip',
      }
      const isAddressValid = addressFormatter.validate({
        addressLine1: cachedApplicationData?.businessAddress1 || '',
        addressLine2: cachedApplicationData?.businessAddress2 || '',
        adminArea1: cachedApplicationData?.businessState || '',
        adminArea2: cachedApplicationData?.businessCity || '',
        postalCode: cachedApplicationData?.businessZip || '',
        countryCode: cachedApplicationData?.countryCode ?? 'US',
      })

      if (isPhoneValid?.errors?.length > 0) {
        clientLogger.info(
          'Clearing prepopulating invalid business phone number for renewal application - Application ID:',
          data?.applicationMetaData?.applicationId,
        )
        fieldsToUpdate.businessPhoneNumber = ''
      }

      if (isAddressValid?.errors?.length > 0) {
        clientLogger.info(
          'Clearing prepopulating invalid business Address data for renewal application - Application ID:',
          data?.applicationMetaData?.applicationId,
        )
        setChangeApplicationFields(true)
        setApplicationPageFields(applicationPage3Fields)
        isAddressValid?.errors?.forEach((error) => {
          const key = addressKeyMap[error.element]
          if (key) {
            fieldsToUpdate[key] = ''
          }
        })
      }
      updateApplicationCachedData(
        {
          ...fieldsToUpdate,
        },
        cachedApplicationData,
      )
    }
  }, [])

  const constructAddressFields = () => {
    const addressFields = {
      addressLine1: cachedApplicationData.businessAddress1,
      addressLine2: cachedApplicationData.businessAddress2,
      addressLine3: `${cachedApplicationData.businessCity}, ${cachedApplicationData.businessState} ${cachedApplicationData.businessZip}`,
    }

    return addressFields
  }

  const updateHomeBasedBusiness = (homeBasedBusinessValue) => {
    if (homeBasedBusinessValue === 'yes') {
      const newBusinessAddress = {
        businessAddress1: cachedApplicationData.homeAddress1,
        businessAddress2: cachedApplicationData.homeAddress2,
        businessCity: cachedApplicationData.city,
        businessState: cachedApplicationData.state,
        businessZip: cachedApplicationData.zip,
      }
      updateApplicationCachedData(newBusinessAddress, cachedApplicationData)
    }
  }

  const focusOnErrors = createDecorator()

  const handleConfirmAddress = (isRecommended) => {
    clientLogger.info(
      `application page 3 submitted with the ${isRecommended} address - Application ID:`,
      data?.applicationMetaData?.applicationId,
    )
    setIsAddressNormalizationDialog(false)
    if (isRecommended === addressDialogConstants.enteredAddress) {
      updateApplicationCachedData(applicationFormData, cachedApplicationData)
      updateApplication({
        variables: {
          updateInput: updateApplicationPayloadGeneratorPage3(
            applicationFormData,
            cachedApplicationData.legalBusinessName,
            data.applicationMetaData.applicationId,
          ),
        },
      })
    } else {
      const applicationData = {
        ...applicationFormData,
        businessAddress1: suggestedAddress?.address?.addressLine1,
        businessAddress2: suggestedAddress?.address?.addressLine2,
        businessState: suggestedAddress?.address?.adminArea1,
        businessCity: suggestedAddress?.address?.adminArea2,
        businessZip: suggestedAddress?.address?.postalCode,
      }
      updateApplicationCachedData(applicationData, cachedApplicationData)
      updateApplication({
        variables: {
          updateInput: updateApplicationPayloadGeneratorPage3(
            applicationData,
            cachedApplicationData.legalBusinessName,
            data.applicationMetaData.applicationId,
          ),
        },
      })
    }
  }

  const renderAddressNormalizationSpinner = () => (
    <div className="normalizeSpinnerWrapper">
      <div className="spinner">
        <SpinnerAdapter />
      </div>
    </div>
  )

  return (
    <>
      {loading || creditApplicationLoading ? (
        <div className="spinner">
          <SpinnerAdapter />
        </div>
      ) : (
        <>
          {data?.eligibilityData?.preApproved && (
            <PreApprovalBanner
              approvalCategory={data?.eligibilityData?.category}
              expiryDate={data?.eligibilityData?.expiryDate}
            />
          )}
        <Form onSubmit={handleApplicationSubmit} decorators={[focusOnErrors]}>
          {({ handleSubmit, hasValidationErrors, values }) => (
            <div className="applicationFormContainer">
              <form onSubmit={handleSubmit}>
                <div className="applicationTextSection">
                  <Row>
                    <Col form="full">
                      <HeadingText size="sm">
                        {getLocalizedText(
                          'application.applicationPages.applicationPage3.headingText',
                        )}
                      </HeadingText>
                    </Col>
                  </Row>
                  <Row>
                    <Col form="full">
                      <BodyText>
                        {getLocalizedText(
                          'application.applicationPages.applicationPage3.checkEligibilityText',
                        )}
                      </BodyText>
                    </Col>
                  </Row>
                </div>
                <div className="applicationFields">
                  <Row>
                    <Col form="full">
                      <BodyText strong data-dd-privacy="mask">
                        {cachedApplicationData?.legalBusinessName}
                      </BodyText>
                    </Col>
                  </Row>
                  {!changeApplicationFields && (
                    <AddressCard
                      addressFields={constructAddressFields()}
                      handleButtonClick={handleAddressOnChange}
                      addressType="Business address"
                    />
                  )}
                  {applicationPageFields.map((applicationField, index) => {
                    if (applicationType === 'app') {
                      updateHomeBasedBusiness(values.homeBasedBusiness)
                    }
                    if (Array.isArray(applicationField)) {
                      return (
                        <Row key={`applicationFieldsRow${index}`}>
                          {applicationField.map((field) => {
                            const { name, ...props } = field
                            return (
                              <Col key={name} form="half">
                                <Field
                                  name={name}
                                  {...props}
                                  initialValue={
                                    cachedApplicationData?.[name] || null
                                  }
                                />
                              </Col>
                            )
                          })}
                        </Row>
                      )
                    }
                    const { name, ...props } = applicationField
                    return (
                      <Row key={name}>
                        <Col form="full">
                          <Field
                            name={name}
                            {...props}
                            initialValue={cachedApplicationData?.[name] || null}
                          />
                        </Col>
                      </Row>
                    )
                  })}
                </div>
                <Button
                  className="ctaSection"
                  size="lg"
                  onClick={() => scrollOnErrors(hasValidationErrors)}
                  type="submit"
                >
                  {getLocalizedText(
                    'application.applicationPages.applicationPage3.continueButtonText',
                  )}
                </Button>
              </form>
              <InfoText />
            </div>
          )}
        </Form>
        </>
      )}
      {addressNormalizationLoading && renderAddressNormalizationSpinner()}
      <AddressConfirmationDialog
        isAddressDialogOpen={isAddressNormalizationDialog}
        setIsAddressDialogOpen={setIsAddressNormalizationDialog}
        confirmAddress={handleConfirmAddress}
        status={suggestedAddress?.status}
        suggestedAddress={suggestedAddress}
        userAddress={userAddress}
        applicationId={data.applicationMetaData.applicationId}
      />
    </>
  )
}

export default ApplicationPage3
